<template>
  <div class="import-labels-container">

    <h3 class="import-labels-title">IMPORT TRACKER</h3>
    <div>
      <div>
        Danger! You are about to perform an action on the system that cannot be
        undone.
      </div>
      <div>
        Your last backup was on:
        <span v-if="backup"> {{ backup.backupDate | formatDateWithTime }}</span>
        <span v-if="!backup">No backups found</span>
      </div>
      <div v-if="!hasABackup()" class="import-labels-backup-now-container">
        To continue, an updated tracker backup (made in the last 24 hours) must
        exist.
        <h4 class="import-labels-backup-now" @click="goToBackup()">
          BACKUP NOW
        </h4>
      </div>
      <div>
        Before uploading the file, please be sure it complies with the following
        rules: <br /><br />

        COLUMN REQUIREMENTS <br />
        Must have the exact column names: <br />
<ul>
  <li>Country</li>
  <li>Organization Name</li>
  <li>Type of award</li>
  <li>Certification Year</li>
  <li>Validity of the Certificate in Years</li>
  <li>Year of Expiration</li>
  <li>Number of Certified Sites</li>
  <li>Type of Organization</li>
  <li>Sector</li>
  <li>Economic Activity</li>
  <li>Multinational Company</li>
  <li>Size</li>
  <li>Female Employees</li>
  <li>Male Employees</li>
</ul>

        <br />

        DATA REQUIREMENTS <br />
        <ul>
          <li>Use your best judgement and please be careful. Double check you're
            uploading the correct file. No duplicates.</li>
          <li>Accented characters are supported. Avoid using typographical and not printable characters (this
            happens specially when copy pasting from word processing
            applications).</li>
          <li>Generally speaking do not try to paste anything other than plain text
            (no tables, no symbols, no emojis, no styling).</li>
        </ul>
      </div>
    </div>

    <b-button
      class="labels-regenerate-regenerate-button"
      variant="primary"
      :disabled="!hasABackup()"
      @click="showModal = true; errorInFile=false; insertedLog=null;success=false"
    >
      IMPORT TRACKERS
    </b-button>

    <h4 class="backup-labels-sub-title">UPLOAD LOGS</h4>

    <b-table :items="logs" :fields="fields" striped>
      <template #cell(cacheDate)="data">
        {{ data.item.cacheDate | formatDateWithTime }}
      </template>
      <template #cell(language)="data">
        {{ langs[data.item.language] }}
      </template>

      <template #cell(actions)="data">
        <b-button
          class="backup-labels-button-outline"
          variant="primary"
          @click="selectBackupToRestore(data.item)"
        >
          Restore
        </b-button>
        <b-button
          class="backup-labels-button-outline"
          variant="primary"
          @click="selectBackupToDelete(data.item)"
        >
          Delete
        </b-button>
      </template>
    </b-table>

    <b-modal
      id="modalInfo"
      v-model="showModal"
      no-close-on-backdrop
      no-close-on-esc
      modal-class="modalInfo"
      title="Import Trackers"
    >
      <div v-if="!errorInFile && !insertedLog && !success">
        <b-form-group label="Select the XLSX file you want to import">
          <b-form-file v-model="file" />
        </b-form-group>
      </div>
      <div v-if="!errorInFile && insertedLog && !success">
        The import file: {{ insertedLog.originalFileName }} has been
        successfully validated. <br />
        Please note that you are creating {{toCreate}} trackers <br />
        <b-checkbox v-model="confirmation">
          I confirm that I understand this action will overwrite this trackers
          in the system.
        </b-checkbox>
        <br />
        If you are sure you want to overwrite the trackers in the system, please
        write the word YES in the field below:
        <b-input v-model="yesInput" />
      </div>
      <div v-if="!errorInFile && success && insertedLog">
        The tracker data has been updated with the file:
        {{ insertedLog.originalFileName }}<br />
        If you made a mistake please roll back using the RESTORE feature
        in the Backup - Restore Language section.
      </div>
      <div v-if="errorInFile">
        The import file failed validated.
        <br />
        Please correct the following problems and try again <br />
        <div class="import-labels-problems">
<ul v-if="errors.length">
  <li v-for="error of errors" style="text-align: start">
    <div v-html="error"></div>
  </li>
</ul>
        </div>
      </div>
      <div slot="modal-footer" class="import-labels-modal-footer">
        <div v-if="!errorInFile && !insertedLog && !success">
          <b-button @click="showModal = false"> Close </b-button>
          <b-button variant="primary" :disabled="!file" @click="postCsv()">
            Import
          </b-button>
        </div>
        <div v-if="!errorInFile && insertedLog && !success">
          <b-btn variant="primary" @click="showModal = false"> Close </b-btn>
          <b-btn
            variant="primary"
            :disabled="!yesInput || yesInput !== 'YES' || !confirmation"
            @click="acceptUpload()"
          >
            Upload file and overwrite trackers
          </b-btn>
        </div>
        <div v-if="!errorInFile && success">
          <b-btn variant="primary" @click="clearModal()"> Close </b-btn>
        </div>
        <div v-if="errorInFile">
          <b-btn variant="primary" @click="clearModal()"> Close </b-btn>
        </div>
      </div>
    </b-modal>
  </div>
</template>


<script>
import Vue from "vue";
import Icon from "vue-awesome/components/Icon";
import "vue-awesome/icons";
import "@/assets/icons/icons";
import { axiosCall } from "../../../../utils/axiosCall";

Vue.component("Icon", Icon);

export default {
  name: "TrackerImport",
  data: function () {
    return {
      showModal: false,
      backup: {},
      file: null,
      fields: ["cacheDate", "originalFileName"],
      toUpdate: 0,
      toCreate: 0,
      total: 0,
      insertedLog: null,
      confirmation: false,
      yesInput: null,
      success: false,
      errorInFile: false,
      logs: [],
      errors:[]
    };
  },
  created: async function () {
    await this.getAllBackups();
    await this.getLogs();
  },
  methods: {
    getAllBackups: async function () {
      await axiosCall({
        url: "TrackerBackups/GetLastBackup",
        method: "GET",
      }).then(async (response) => {
        this.backup = response.data;
      });
    },
    hasABackup() {
      if (this.backup && this.backup.backupDate) {
        var d = new Date();
        d.setHours(d.getHours() - 24);
        return new Date(this.backup.backupDate).getTime() > d.getTime();
      }
      return false;
    },
    getLogs: async function () {
      await axiosCall({ url: "TrackerImport/log", method: "GET" }).then(
        async (response) => {
          this.logs = response.data;
        }
      );
    },
    goToBackup() {
      this.$router.push("/tracker-backup");
    },
    postCsv: async function () {
      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("test", "testValue");
      await axiosCall({
        url: `/TrackerImport`,
        method: "POST",
        headers: { "content-type": "multipart/form-data" },
        data: formData,
      }).then(async (response) => {
        if (response.data && !response.data.withErrors) {
          this.insertedLog = response.data.insertedLog;
          this.toUpdate = response.data.toUpdate;
          this.toCreate = response.data.total;
          this.total = response.data.total;
        } else {
          this.errorInFile = true;
          this.errors = response.data.errorsMessages
        }
      });
    },
    acceptUpload: async function () {
      await axiosCall({
        url: `/TrackerImport/Accept/${this.insertedLog.id}`,
        method: "GET",
      }).then(async () => {
        this.success = true;
      });
    },
    clearModal: function () {
      this.showModal = false;
      this.insertedLog = null;
      this.success = false;
      this.errorInFile = false;
    },
  },
};
</script>
<style lang="scss">
.import-labels {
  &-container {
    padding: 30px 60px;
  }

  &-title {
    margin-bottom: 15px;
  }

  &-regenerate-button {
    padding: 15px;
    margin-top: 20px;
    margin-bottom: 20px;
    border-radius: 5px !important;
  }

  &-last-time {
    font-size: 0.8rem;
  }

  &-backup-now {
    text-decoration: underline;
    cursor: pointer;
    display: inline-block;
    margin-left: 7px;

    &-container {
      color: #a70404;
    }
  }

  &-problems {
    color: #a70404;
  }

  &-modal {
    &-footer {
      justify-content: flex-end !important;

      .btn {
        margin-left: 10px;
        border-radius: 5px !important;
      }
    }
  }
}
</style>
